import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AuthImage } from "@/components/AuthImage";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFileDrop } from "@/lib/useFileDrop";
import { FileText, Download, Trash2, CheckCircle2, Banknote, Receipt, Upload, UploadCloud, Plus, X } from "lucide-react";
import { toast } from "sonner";

const iso = (d) => d.toISOString().slice(0, 10);
const fortnightAgo = () => { const d = new Date(); d.setDate(d.getDate() - 14); return iso(d); };
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function ContractorInvoices() {
  const { user } = useAuth();
  const isOffice = user.role !== "field";
  const [invoices, setInvoices] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = () => {
    api.get("/contractor/invoices").then((r) => setInvoices(r.data.items || [])).catch((e) => {
      toast.error(e.response?.data?.detail || "Failed to load invoices");
      setInvoices([]);
    });
  };
  useEffect(load, []);

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/contractor/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contractor-invoice-${inv.invoice_number || inv.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  const setStatus = async (inv, status) => {
    try { await api.patch(`/contractor/invoices/${inv.id}/status`, { status }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (inv) => {
    if (!window.confirm("Delete this invoice? Its timesheets become invoiceable again.")) return;
    try { await api.delete(`/contractor/invoices/${inv.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto pb-24" data-testid="contractor-invoices-page">
      <PageHeader
        tag="/ Contractors"
        title={isOffice ? "Contractor invoices" : "My invoices"}
        subtitle={isOffice
          ? "Invoices your contractors have raised from their approved timesheets."
          : "Invoice the company straight from your approved hours."}
      />

      {!isOffice && <MyDetails />}
      {!isOffice && <NewInvoice onCreated={load} />}

      {isOffice && (
        <div className="mb-5">
          <Button onClick={() => setUploadOpen(true)}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
                  data-testid="upload-contractor-invoice-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Upload contractor invoice
          </Button>
          <p className="text-[11px] text-zinc-500 mt-1.5">For contractors who send a PDF instead of logging hours in Fieldlink.</p>
        </div>
      )}
      {isOffice && <ExternalInvoiceDialog open={uploadOpen} onOpenChange={setUploadOpen} onCreated={load} />}

      {!invoices ? (
        <div className="text-xs text-zinc-500 font-mono">Loading…</div>
      ) : invoices.length === 0 ? (
        <EmptyState title="No invoices yet"
                    description={isOffice ? "Contractor invoices will land here for approval and payment." : "Build your first invoice above."} />
      ) : (
        <div className="space-y-3 mt-2">
          {invoices.map((inv) => (
            <div key={inv.id} className="hard-card p-4" data-testid={`invoice-${inv.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <span className="w-10 h-10 bg-[#09090B] text-[#FF5F15] flex items-center justify-center shrink-0">
                  <Receipt className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm">
                    {inv.invoice_number ? `#${inv.invoice_number}` : `#${inv.id.slice(0, 8).toUpperCase()}`}
                    {isOffice && <span className="text-zinc-500 font-normal"> · {inv.contractor_name}</span>}
                    {inv.source === "external" && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest border border-zinc-300 bg-zinc-50 text-zinc-600 align-middle">Uploaded PDF</span>
                    )}
                  </div>
                  <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
                    {inv.source === "external" ? (
                      <>{inv.invoice_date || (inv.created_at || "").slice(0, 10)} · {(inv.allocations?.length || 0)} job{(inv.allocations?.length === 1) ? "" : "s"} · {inv.hours}h{inv.gst_registered && inv.gst ? ` · incl. GST ${money(inv.gst)}` : ""}</>
                    ) : (
                      <>{inv.date_from} → {inv.date_to} · {inv.hours}h × {money(inv.rate)}{inv.gst_registered && ` · incl. GST ${money(inv.gst)}`}</>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold text-lg" data-testid={`invoice-total-${inv.id}`}>{money(inv.total)}</div>
                  <span className={`inline-block px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest border ${
                    inv.status === "paid" ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : inv.status === "approved" ? "border-sky-500 bg-sky-50 text-sky-700"
                        : "border-amber-400 bg-amber-50 text-amber-700"}`}
                        data-testid={`invoice-status-${inv.id}`}>
                    {inv.status}
                  </span>
                </div>
              </div>
              {inv.source === "external" && inv.allocations?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`invoice-allocations-${inv.id}`}>
                  {inv.allocations.map((a, i) => (
                    <span key={i} className="text-[10px] font-mono bg-zinc-100 text-zinc-700 rounded px-2 py-0.5">
                      {(a.job_title || a.job_number || "Job")} · {Number(a.hours || 0).toFixed(1)}h · {money(a.amount)}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => downloadPdf(inv)}
                        className="rounded-sm uppercase tracking-wider font-bold text-[10px]"
                        data-testid={`invoice-pdf-${inv.id}`}>
                  <Download className="w-3.5 h-3.5 mr-1" /> PDF
                </Button>
                {isOffice && inv.status === "submitted" && (
                  <Button size="sm" onClick={() => setStatus(inv, "approved")}
                          className="rounded-sm bg-sky-600 hover:bg-sky-700 text-white uppercase tracking-wider font-bold text-[10px]"
                          data-testid={`invoice-approve-${inv.id}`}>
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                )}
                {isOffice && inv.status === "approved" && (
                  <Button size="sm" onClick={() => setStatus(inv, "paid")}
                          className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white uppercase tracking-wider font-bold text-[10px]"
                          data-testid={`invoice-paid-${inv.id}`}>
                    <Banknote className="w-3.5 h-3.5 mr-1" /> Mark paid
                  </Button>
                )}
                {(isOffice || inv.status === "submitted") && (
                  <Button size="sm" variant="ghost" onClick={() => remove(inv)}
                          className="rounded-sm text-zinc-500 hover:text-red-600 uppercase tracking-wider font-bold text-[10px] ml-auto"
                          data-testid={`invoice-delete-${inv.id}`}>
                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ExternalInvoiceDialog({ open, onOpenChange, onCreated }) {
  const [jobs, setJobs] = useState([]);
  const [pdf, setPdf] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ contractor_name: "", invoice_number: "", invoice_date: iso(new Date()), gst_registered: false, gst: "", total: "", notes: "" });
  const [rows, setRows] = useState([{ job_id: "", hours: "", amount: "", notes: "" }]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "contractor_invoice");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPdf({ path: data.storage_path, name: file.name });
      toast.success("PDF attached");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const { dragging, dropProps } = useFileDrop((files) => uploadFile(files?.[0]));

  useEffect(() => {
    if (!open) {
      setPdf(null); setUploading(false);
      setForm({ contractor_name: "", invoice_number: "", invoice_date: iso(new Date()), gst_registered: false, gst: "", total: "", notes: "" });
      setRows([{ job_id: "", hours: "", amount: "", notes: "" }]);
      return;
    }
    api.get("/jobs").then((r) => setJobs((r.data || []).filter((j) => j.status !== "completed"))).catch(() => {});
  }, [open]);

  const setF = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const setRow = (i, k, v) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => setRows((rs) => [...rs, { job_id: "", hours: "", amount: "", notes: "" }]);
  const rmRow = (i) => setRows((rs) => rs.filter((_, idx) => idx !== i));

  const allocTotal = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const allocHours = rows.reduce((s, r) => s + (Number(r.hours) || 0), 0);
  const totalNum = Number(form.total) || 0;
  const mismatch = totalNum > 0 && Math.abs(allocTotal - totalNum) > 0.01;

  const submit = async () => {
    if (!form.contractor_name.trim()) return toast.error("Contractor name is required");
    if (totalNum <= 0) return toast.error("Enter the invoice total");
    const allocations = rows.filter((r) => r.job_id).map((r) => ({
      job_id: r.job_id, hours: Number(r.hours) || 0, amount: Number(r.amount) || 0, notes: r.notes || null,
    }));
    setBusy(true);
    try {
      await api.post("/contractor/invoices/external", {
        contractor_name: form.contractor_name.trim(),
        invoice_number: form.invoice_number.trim() || null,
        invoice_date: form.invoice_date || null,
        pdf_path: pdf?.path || null,
        gst_registered: form.gst_registered,
        gst: Number(form.gst) || 0,
        total: totalNum,
        notes: form.notes || null,
        allocations,
      });
      toast.success("Contractor invoice saved");
      onOpenChange(false);
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="external-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Upload contractor invoice</DialogTitle>
          <DialogDescription>For a contractor who sends a PDF. Attach it, then allocate their hours & cost to jobs.</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Contractor name *</Label>
            <Input value={form.contractor_name} onChange={setF("contractor_name")} placeholder="e.g. ABC Earthworks Ltd" className="mt-1.5 rounded-sm border-zinc-400" data-testid="ext-contractor-name" />
          </div>
          <div>
            <Label className="label-tag">Their invoice #</Label>
            <Input value={form.invoice_number} onChange={setF("invoice_number")} placeholder="INV-1234" className="mt-1.5 rounded-sm border-zinc-400" data-testid="ext-invoice-number" />
          </div>
          <div>
            <Label className="label-tag">Invoice date</Label>
            <Input type="date" value={form.invoice_date} onChange={setF("invoice_date")} className="mt-1.5 rounded-sm border-zinc-400" data-testid="ext-invoice-date" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="label-tag">GST amount</Label>
              <Input type="number" step="0.01" min="0" value={form.gst} onChange={setF("gst")} placeholder="0.00" className="mt-1.5 rounded-sm border-zinc-400" data-testid="ext-gst" />
            </div>
            <div>
              <Label className="label-tag">Invoice total *</Label>
              <Input type="number" step="0.01" min="0" value={form.total} onChange={setF("total")} placeholder="0.00" className="mt-1.5 rounded-sm border-zinc-400" data-testid="ext-total" />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-1">
          <Switch checked={form.gst_registered} onCheckedChange={(v) => setForm({ ...form, gst_registered: v })} data-testid="ext-gst-switch" />
          <span className="text-xs font-bold uppercase tracking-wider">Contractor is GST registered</span>
        </div>

        {/* PDF upload */}
        <div>
          <Label className="label-tag">Contractor's PDF</Label>
          <div {...dropProps} onClick={() => inputRef.current?.click()}
               className={`mt-1.5 flex flex-col items-center justify-center gap-1.5 rounded-sm border-2 border-dashed px-4 py-5 text-center cursor-pointer transition-colors ${dragging ? "border-[#FF5F15] bg-orange-50" : "border-zinc-300 hover:border-[#FF5F15] hover:bg-orange-50/40"}`}
               data-testid="ext-pdf-dropzone">
            <UploadCloud className={`w-6 h-6 ${dragging ? "text-[#FF5F15]" : "text-zinc-400"}`} />
            {uploading ? <span className="text-sm text-zinc-600">Uploading…</span>
              : pdf ? <span className="text-sm font-medium flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-green-600" /> {pdf.name}</span>
                : <span className="text-sm text-zinc-600"><span className="font-semibold text-[#FF5F15]">Drag & drop</span> the invoice, or <span className="underline">choose file</span></span>}
            <input ref={inputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="ext-pdf-input" />
          </div>
        </div>

        {/* Allocations */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="label-tag">Allocate to jobs</Label>
            <Button size="sm" variant="outline" onClick={addRow} className="rounded-sm h-7 text-[10px] uppercase font-bold tracking-wider" data-testid="ext-add-alloc">
              <Plus className="w-3 h-3 mr-1" /> Add job
            </Button>
          </div>
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`ext-alloc-row-${i}`}>
                <Select value={r.job_id} onValueChange={(v) => setRow(i, "job_id", v)}>
                  <SelectTrigger className="rounded-sm border-zinc-400 h-9 flex-1" data-testid={`ext-alloc-job-${i}`}>
                    <SelectValue placeholder="Select job…" />
                  </SelectTrigger>
                  <SelectContent className="rounded-sm max-h-64">
                    {jobs.map((j) => <SelectItem key={j.id} value={j.id}>{j.job_number ? `${j.job_number} · ` : ""}{j.title}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" step="0.25" min="0" value={r.hours} onChange={(e) => setRow(i, "hours", e.target.value)} placeholder="hrs" className="rounded-sm border-zinc-400 h-9 w-20" data-testid={`ext-alloc-hours-${i}`} />
                <div className="relative w-28">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">$</span>
                  <Input type="number" step="0.01" min="0" value={r.amount} onChange={(e) => setRow(i, "amount", e.target.value)} placeholder="0.00" className="rounded-sm border-zinc-400 h-9 pl-5" data-testid={`ext-alloc-amount-${i}`} />
                </div>
                <button onClick={() => rmRow(i)} className="text-zinc-400 hover:text-red-600 shrink-0" data-testid={`ext-alloc-remove-${i}`}><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between text-xs font-mono">
            <span className="text-zinc-500">Allocated: {allocHours.toFixed(2)}h · {money(allocTotal)}</span>
            {mismatch && (
              <span className="text-amber-600 font-bold" data-testid="ext-alloc-mismatch">
                ⚠ Allocated {money(allocTotal)} ≠ invoice total {money(totalNum)}
              </span>
            )}
          </div>
        </div>

        <div>
          <Label className="label-tag">Notes</Label>
          <Textarea value={form.notes} onChange={setF("notes")} rows={2} placeholder="Optional" className="mt-1.5 rounded-sm border-zinc-400" data-testid="ext-notes" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm uppercase tracking-wider font-bold">Cancel</Button>
          <Button onClick={submit} disabled={busy || uploading} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold" data-testid="ext-save-btn">
            {busy ? "Saving…" : "Save invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MyDetails() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  useEffect(() => {
    api.get("/contractor/profile").then((r) => setF({
      contractor_rate: r.data.contractor_rate ?? "",
      gst_registered: !!r.data.gst_registered,
      gst_number: r.data.gst_number || "",
      bank_account: r.data.bank_account || "",
      logo_path: r.data.logo_path || "",
    })).catch(() => setF({ contractor_rate: "", gst_registered: false, gst_number: "", bank_account: "", logo_path: "" }));
  }, []);

  if (!f) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/contractor/profile", {
        contractor_rate: f.contractor_rate === "" ? null : Number(f.contractor_rate),
        gst_registered: f.gst_registered,
        gst_number: f.gst_number,
        bank_account: f.bank_account,
      });
      toast.success("Details saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", "contractor_logo");
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/contractor/profile", { logo_path: data.storage_path });
      setF((x) => ({ ...x, logo_path: data.storage_path }));
      toast.success("Logo saved — it'll show on your invoices");
    } catch (err) { toast.error(err.response?.data?.detail || "Logo upload failed"); }
    finally { setLogoBusy(false); e.target.value = ""; }
  };

  const removeLogo = async () => {
    try {
      await api.patch("/contractor/profile", { logo_path: "" });
      setF((x) => ({ ...x, logo_path: "" }));
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="hard-card p-5 mb-4" data-testid="contractor-details-card">
      <div className="label-tag mb-3">My details</div>
      <div className="grid sm:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="label-tag">Hourly rate ($)</Label>
          <Input type="number" step="0.5" min="0" value={f.contractor_rate}
                 onChange={(e) => setF({ ...f, contractor_rate: e.target.value })}
                 className="mt-1.5 rounded-sm border-zinc-400" data-testid="contractor-rate-input" />
        </div>
        <div className="flex items-center gap-2 h-10">
          <Switch checked={f.gst_registered} onCheckedChange={(v) => setF({ ...f, gst_registered: v })}
                  data-testid="contractor-gst-switch" />
          <span className="text-xs font-bold uppercase tracking-wider">GST registered</span>
        </div>
        <div>
          <Label className="label-tag">GST number</Label>
          <Input value={f.gst_number} disabled={!f.gst_registered}
                 onChange={(e) => setF({ ...f, gst_number: e.target.value })}
                 placeholder="123-456-789"
                 className="mt-1.5 rounded-sm border-zinc-400" data-testid="contractor-gst-number-input" />
        </div>
        <div>
          <Label className="label-tag">Bank account (shows on invoice)</Label>
          <Input value={f.bank_account}
                 onChange={(e) => setF({ ...f, bank_account: e.target.value })}
                 placeholder="12-3456-0123456-00"
                 className="mt-1.5 rounded-sm border-zinc-400" data-testid="contractor-bank-input" />
        </div>
      </div>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {f.logo_path ? (
            <>
              <AuthImage path={f.logo_path} alt="Logo" className="h-10 w-auto max-w-[120px] object-contain border border-zinc-200 bg-white p-1" data-testid="contractor-logo-preview" />
              <button onClick={removeLogo} className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-red-600" data-testid="contractor-logo-remove">Remove</button>
            </>
          ) : (
            <span className="text-[11px] text-zinc-500">No logo yet</span>
          )}
          <label className="cursor-pointer text-[10px] font-bold uppercase tracking-wider border border-zinc-400 px-3 py-2 hover:border-[#FF5F15] hover:text-[#FF5F15]">
            {logoBusy ? "Uploading…" : "Upload logo"}
            <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="contractor-logo-input" />
          </label>
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Shown at the top of your invoice PDF</span>
        </div>
        <Button onClick={save} disabled={saving}
                className="rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white uppercase tracking-wider font-bold text-xs h-10 ml-auto"
                data-testid="contractor-details-save">
          {saving ? "Saving…" : "Save details"}
        </Button>
      </div>
    </div>
  );
}

function NewInvoice({ onCreated }) {
  const [from, setFrom] = useState(fortnightAgo());
  const [to, setTo] = useState(iso(new Date()));
  const [preview, setPreview] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/contractor/profile")
      .then((r) => setInvoiceNumber((cur) => cur || String(r.data.next_invoice_number || 1)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!from || !to) return;
    api.get(`/contractor/unbilled?date_from=${from}&date_to=${to}`)
      .then((r) => setPreview(r.data))
      .catch(() => setPreview(null));
  }, [from, to]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/contractor/invoices", { date_from: from, date_to: to, invoice_number: invoiceNumber, notes });
      toast.success("Invoice submitted to the office");
      setNotes("");
      api.get("/contractor/profile").then((r) => setInvoiceNumber(String(r.data.next_invoice_number || ""))).catch(() => setInvoiceNumber(""));
      setPreview(null);
      api.get(`/contractor/unbilled?date_from=${from}&date_to=${to}`).then((r) => setPreview(r.data)).catch(() => {});
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create the invoice"); }
    finally { setBusy(false); }
  };

  return (
    <div className="hard-card p-5 mb-6" data-testid="new-invoice-card">
      <div className="label-tag mb-3 flex items-center gap-1.5"><FileText className="w-3 h-3" /> New invoice</div>
      <div className="grid sm:grid-cols-4 gap-3">
        <div>
          <Label className="label-tag">From</Label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                 className="mt-1.5 rounded-sm border-zinc-400" data-testid="invoice-from-input" />
        </div>
        <div>
          <Label className="label-tag">To</Label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                 className="mt-1.5 rounded-sm border-zinc-400" data-testid="invoice-to-input" />
        </div>
        <div>
          <Label className="label-tag">Your invoice #</Label>
          <Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="INV-001"
                 className="mt-1.5 rounded-sm border-zinc-400" data-testid="invoice-number-input" />
        </div>
        <div>
          <Label className="label-tag">Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional"
                 className="mt-1.5 rounded-sm border-zinc-400" data-testid="invoice-notes-input" />
        </div>
      </div>

      {preview && (
        <div className="mt-4 border border-zinc-200 bg-zinc-50 p-3" data-testid="invoice-preview">
          {preview.items.length === 0 ? (
            <p className="text-xs text-zinc-500">No approved, un-invoiced timesheets in this period.</p>
          ) : (
            <>
              <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">
                {preview.items.length} timesheet{preview.items.length === 1 ? "" : "s"} ready to bill
              </div>
              <div className="divide-y divide-zinc-100 max-h-44 overflow-y-auto">
                {preview.items.map((t) => (
                  <div key={t.id} className="py-1.5 flex items-center justify-between text-xs">
                    <span className="font-mono">{t.date}</span>
                    <span className="flex-1 mx-3 truncate text-zinc-600">{t.job_title || "—"}</span>
                    <span className="font-mono font-bold">{Number(t.hours || 0).toFixed(2)}h</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-zinc-200 flex items-center justify-between text-sm">
                <span className="font-bold uppercase tracking-wider text-xs">
                  {preview.hours}h × {money(preview.rate)}
                </span>
                <span className="font-mono font-bold" data-testid="invoice-preview-subtotal">{money(preview.subtotal)} + GST if registered</span>
              </div>
            </>
          )}
        </div>
      )}

      <Button onClick={create} disabled={busy || !preview || preview.items.length === 0}
              className="mt-4 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
              data-testid="invoice-create-btn">
        {busy ? "Submitting…" : "Submit invoice to office"}
      </Button>
    </div>
  );
}
